import {useDisclosure} from '@mantine/hooks';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {SiteSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {ActiveInactivePills} from 'src/shared/constants/PillConstants';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {ActiveStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {SidebarLinkClients} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  useDeleteSiteMutation,
  useGetSiteListV2Query,
} from 'src/redux/queries/site';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface SiteTableProps {
  /** Filters the list to sites belonging to this client (e.g. embedded on ClientView). */
  clientId?: number;
  /** Distinguishes persisted table state (column order/filters) across embeddings. */
  identifier?: string;
  /** Renders the page-level title/subtitle. Off when embedded within another entity's view. */
  showHeader?: boolean;
}

export const SiteTable: React.FC<SiteTableProps> = ({
  clientId,
  identifier = 'site',
  showHeader = true,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [deleteTarget, setDeleteTarget] = useState<SiteSimpleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteSite] = useDeleteSiteMutation();

  const showClientColumn = clientId === undefined;

  const columns = useMemo<MRT_ColumnDef<SiteSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'siteNumber',
        header: 'Site Number',
      },
      ...(showClientColumn
        ? [
            {
              accessorKey: 'clientName',
              header: 'Client',
            } as MRT_ColumnDef<SiteSimpleResponse>,
          ]
        : []),
      {
        id: 'location',
        accessorFn: row => [row.city, row.state].filter(Boolean).join(', '),
        header: 'City/State',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'timeZone',
        header: 'Time Zone',
      },
    ],
    [showClientColumn]
  );

  const tableState = useTableState({
    identifier,
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'active',
        value: ['true'],
      },
    ],
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetSiteListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
      clientId,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleDelete = async () => {
    if (!deleteTarget) return;

    confirmDeleteHandlers.close();
    setIsDeleting(true);

    try {
      await deleteSite(deleteTarget.id).unwrap();
      setAlert(
        `${deleteTarget.name ?? 'Site'} was successfully deleted.`,
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeleting(false);
    setDeleteTarget(undefined);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => navigate(PathUtility.SitesView(row.original.id))}
          />
          <PermissionGuard
            module={ModuleEnum.Clients}
            operation={PermissionOperationEnum.Delete}>
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => {
                  setDeleteTarget(row.original);
                  confirmDeleteHandlers.open();
                }}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          </PermissionGuard>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Clients}
              operation={PermissionOperationEnum.Create}>
              <TableToolbar.Action
                variant="create"
                onClick={() =>
                  navigate(
                    clientId !== undefined
                      ? `${PathUtility.SitesCreate()}?clientId=${clientId}`
                      : PathUtility.SitesCreate()
                  )
                }>
                New Site
              </TableToolbar.Action>
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      {showHeader && (
        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkClients.icon}
            subtitle="Manage job sites and property addresses">
            Sites
          </PageHeader.Title>
        </PageHeader>
      )}
      {table}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title="Are you sure you want to delete this site?"
        body="This action cannot be undone."
      />
    </>
  );
};
